import React, { useEffect, useState } from 'react';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import axios from 'axios';
import Spinner from '../../utils/Spinner';
import { getUser } from '../../utils/tokens';
import { find, indexOf } from 'rambda';

import InlineEditor from 'ckeditor5-custom-build/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';

import { toast } from 'react-toastify';
import FileIcon from '../../utils/FileIcon';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { formatBytes, getExtension, getImage, submitFileAsset } from '../../utils/FileHelper';
import SelectBox from '../../utils/SelectBox';
import { useAuthDispatch } from '../../contexts/auth';
import DropUpload from '../../utils/Dropupload';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const assetUploadURL = `/resource/v1/resources/files`;
const assetDeleteURL = id => `/resource/v1/resources/attachment/${id}`;
const createUrl = clientId => `/resource/v1/resources/clients/${clientId}`;
const getResourceURL = (resourceId, clientId) => `/resource/v1/resources/${resourceId}/clients/${clientId}`;
const updateUrl = (resourceId, clientId) => `/resource/v1/resources/${resourceId}/clients/${clientId}`;
const deleteURL = id => `/resource/v1/resources/${id}`;
const categoriesUrl = clientId => `resource/v1/resource-categories/clients/${clientId}`;

const ResourceCreate = ({ resource }) => {
	let navigate = useNavigate();
	const auth = useAuthDispatch();
	const currentUser = getUser();

	const [categories, setCategories] = useState(null);
	const [description, setDescription] = useState('');
	const [meta, setMeta] = useState('');
	const [enabled, setEnabled] = useState(true);
	const [global, setGlobal] = useState(false);
	const [type, setType] = useState('LINK');
	const [uploading, setUploading] = useState(false);
	const [document, setDocument] = useState(null);
	const [resourceDocument, setResourceDocument] = useState(null);
	const [documentLabel, setDocumentLabel] = useState('');
	const [documentSize, setDocumentSize] = useState(null);
	const [documentLink, setDocumentLink] = useState('');
	const [documentFilename, setDocumentFilename] = useState(null);
	const [documentExtension, setDocumentExtension] = useState(null);
	const [attachmentId, setAttachmentId] = useState(null);
	const [resourceImageId, setResourceImageId] = useState(null);
	const [resourceImageUrl, setResourceImageUrl] = useState(null);
	const [category, setCategory] = useState({ id: 0, title: 'NONE' });
	const [valid, setValid] = useState(false);
	const [showResourceImage, setShowResourceImage] = useState(false);
	const [selectCategory, setSelectCategory] = useState(false);

	useEffect(() => {
		const fetchCategories = async () => {
			const response = await axios({
				method: 'GET',
				url: categoriesUrl(currentUser.clientIdNum),
			});

			if (response.status !== 200) {
				setCategories([{ id: -1, title: 'No categories' }]);
			} else {
				setCategories(response.data);
			}
			setCategory({ id: -1, title: 'No category' });
		};

		fetchCategories().catch(console.error);
	}, []);

	useEffect(() => {
		if (resource) {
			setMeta(resource.meta);
			setType(resource.type);
			setEnabled(resource.enabled);
			setGlobal(resource.global);

			if (resource.imageId) {
				getImage({ id: resource.imageId, resourceId: resource.id, setImgUrl: setResourceImageUrl });
				setResourceImageId(resource.imageId);
			}

			if (categories) {
				setCategory(categories[0]);
			}
			if (resource.resourceCategory) {
				if (categories) {
					let cat = find(item => {
						return item.id === resource.resourceCategory.id;
					}, categories);

					setCategory(cat);
				}
			}

			if (resource.type === 'ATTACHMENT' && resource.resourceAttachments.length > 0) {
				resource.resourceAttachments[0].fileName = resource.resourceAttachments[0].label;
				setDocument(resource.resourceAttachments[0]);
				setDocumentLabel(resource.resourceAttachments[0].label);
				setDocumentSize(resource.resourceAttachments[0].size);
				setDocumentFilename(resource.resourceAttachments[0].filename);
				setDocumentExtension(resource.resourceAttachments[0].extension);
				setAttachmentId(resource.resourceAttachments[0].key.s3ResourceId);
			} else if (resource.type === 'LINK') {
				setDocumentLabel(resource.resourceLinks[0].name);
				setDocumentLink(resource.resourceLinks[0].link);
			} else if (resource.type === 'INLINE') {
				setDocumentLabel(resource.name);
				setDescription(resource.inline);
			}

			verifyForm(resource.type);
		} else {
			setSelectCategory(true);
			if (currentUser.scope.includes('admin')) {
				setGlobal(true);
				setEnabled(true);
			}
		}
	}, [resource, categories]);

	useEffect(() => {
		if (resourceDocument) {
			setResourceImageId(resourceDocument.id);
			if (resource) {
				getImage({ id: resourceDocument.id, resourceId: resource.id, setImgUrl: setResourceImageUrl });
			} else {
				getImage({ id: resourceDocument.id, resourceId: resourceDocument.id, setImgUrl: setResourceImageUrl });
			}
		}
	}, [resourceDocument, resource]);

	useEffect(() => {}, [type]);

	function handleMetaChange(e) {
		setMeta(e.target.value);
	}

	function handleEnable() {
		setEnabled(!enabled);
	}

	function handleGlobal() {
		setGlobal(!global);
	}

	function handleRadioChange(event) {
		setType(event.target.value);
		verifyForm(event.target.value);
	}

	function handleDocLabelChange(e) {
		setDocumentLabel(e.target.value);
		if ((type === 'LINK' || type === 'ATTACHMENT') && e.target.value.length < 1) {
			setValid(false);
		} else {
			verifyForm(type);
		}
	}

	function handleDocLinkChange(e) {
		setDocumentLink(e.target.value);
		if (type === 'LINK' && e.target.value.length < 1) {
			setValid(false);
		} else {
			verifyForm(type);
		}
	}

	const handleAttachmentTrash = async e => {
		let url = assetDeleteURL(attachmentId);
		if (resource) {
			url = assetDeleteURL(attachmentId) + '?resourceId=' + resource.id;
		}

		await axios({
			method: 'DELETE',
			url: url,
		});
		setDocument(null);
		setDocumentSize(null);
		setAttachmentId(null);
		setDocumentExtension(null);
		setDocumentFilename(null);

		setValid(false);
	};

	const handleResImageTrash = async e => {
		let url = assetDeleteURL(resourceImageId);
		if (resource) {
			url = assetDeleteURL(resourceImageId) + '?resourceId=' + resource.id;
		}
		await axios({
			method: 'DELETE',
			url: url,
		}).then(() => {
			// remove img from UI
			setResourceImageId(null);
			setResourceImageUrl(null);
			toast(
				'Resource image deleted',
				{
					autoClose: 3000,
					type: toast.TYPE.SUCCESS,
				},
				() => {
					toast('Resource image not deleted', {
						autoClose: 6000,
						type: toast.TYPE.ERROR,
					});
				},
			);
		});
	};

	/**
	 *
	 * @param e
	 * @returns {Promise<void>}
	 */
	const submitFile = async e => {
		await submitFileAsset({
			assetUploadURL,
			e,
			documentLabel,
			setUploading,
			setDocument,
			setDocumentLabel,
			setDocumentSize,
			setAttachmentId,
			setDocumentFilename,
			setDocumentExtension,
			verifyForm,
		}).then(() => {
			if (documentLabel.length > 0) {
				setValid(true);
			}
		});
	};

	function submitResourceImageFile(e) {
		submitFileAsset({
			assetUploadURL: assetUploadURL,
			e: e,
			setDocument: setResourceDocument,
			setAttachmentId: setResourceImageId,
		}).then(
			response => {
				console.log('return from submit resource img');
				setShowResourceImage(false);
			},
			error => {
				console.log(error);
			},
		);
	}

	/**
	 *
	 */
	function handleCancelClick() {
		navigate(-1);
	}

	/**
	 *
	 */
	function handleSubmitClick(e) {
		e.preventDefault();

		const data = {
			meta: meta,
			enabled: enabled ? 'true' : 'false',
			type: type,
			label: documentLabel,
			global: global ? 'true' : 'false',
		};

		if (type === 'LINK') {
			data.link = documentLink;
		} else if (type === 'ATTACHMENT') {
			if (!attachmentId) {
				toast('Resource not saved.  Enter all fields', {
					autoClose: 6000,
					type: toast.TYPE.ERROR,
				});
				return;
			} else {
				data.size = documentSize;
				data.attachmentId = attachmentId;
				data.contentType = document.contentType;
				data.extension = documentExtension;
				data.filename = documentFilename;
			}
		} else if (type === 'INLINE') {
			data.inline = description;
			data.name = documentLabel;
		}

		if (category && category.id > 0) {
			data.resourceCategory = { id: category.id };
		}

		if (resourceImageId) {
			data.imageId = resourceImageId;
		}

		let theUrl = resource ? updateUrl(resource.id, currentUser.clientIdNum) : createUrl(currentUser.clientIdNum);
		let theMethod = resource ? 'PATCH' : 'POST';

		axios({
			method: theMethod,
			url: theUrl,
			data: data,
		}).then(
			resp => {
				toast('Resource saved', {
					autoClose: 6000,
					type: toast.TYPE.SUCCESS,
				});
				navigate(-1);
			},
			error => {
				console.log(error);
				toast('Resource not saved', {
					autoClose: 6000,
					type: toast.TYPE.ERROR,
				});
			},
		);
	}

	/**
	 *
	 * @returns {boolean}
	 */
	function verifyForm(selectedType) {
		let verified = false;

		if (selectedType === 'LINK' && documentLink.length > 0 && documentLabel.length > 0) {
			verified = true;
		} else if (selectedType === 'ATTACHMENT' && documentLabel.length > 0 && attachmentId) {
			verified = true;
		} else if (selectedType === 'INLINE') {
			verified = true;
		}

		setValid(verified);
		return verified;
	}

	function handleDeleteClick() {
		axios({
			method: 'DELETE',
			url: deleteURL(resource.id),
		}).then(
			() => {
				navigate(-1);
			},
			error => {
				console.log(error);
			},
		);
	}

	function handleViewPdf() {
		navigate('pdf/' + attachmentId);
	}

	function handleBack() {
		navigate(-1);
	}

	return (
		<form>
			<div className="px-4 space-y-8 divide-y divide-gray-200">
				<div className="pt-8">
					{!auth.isIframe() && (
						<>
							<div className="pb-4" onClick={handleBack}>
								<span className="text-indigo-600">
									<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
								</span>
							</div>
						</>
					)}

					<div>
						<h3 className="text-lg font-medium leading-6 text-gray-900">Resource Details</h3>
					</div>

					<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
						{categories && (
							<>
								<div className="sm:col-span-3">
									{category.id !== 0 ? (
										<>
											{!selectCategory ? (
												<>
													<span className="text-sm font-medium text-gray-700">Category</span>
													<span
														onClick={() => {
															setSelectCategory(true);
														}}
														className="pl-4 text-blue-600 underline decoration-solid">
														{category.title}
													</span>
												</>
											) : (
												<SelectBox
													label="Select Category"
													choices={categories}
													selected={category}
													setSelected={setCategory}
												/>
											)}
										</>
									) : (
										<SelectBox
											label="Select Category"
											choices={categories}
											selected={category}
											setSelected={setCategory}
										/>
									)}
								</div>
							</>
						)}

						<div className="sm:col-span-6 bg-white p-2 rounded-lg border-2 border-slate-300">
							<div className="sm:col-span-6">
								<div className="relative">
									<div className="absolute inset-0 flex items-center" aria-hidden="true">
										<div className="w-full border-t border-gray-300" />
									</div>
									<div className="relative flex justify-center">
										<span className="bg-white px-3 text-md font-medium text-gray-500">
											Card Image
										</span>
									</div>
								</div>
							</div>

							<div className="sm:col-span-6">
								<label htmlFor="photo" className="block text-sm font-medium text-gray-700">
									Photo
								</label>
								<div className="mt-1 flex items-center">
									<span className="h-28 w-28 overflow-hidden rounded-sm bg-gray-100">
										{resourceImageUrl && (
											<img
												src={resourceImageUrl}
												alt="Resource"
												className="object-fill h-48 w-96 ..."
											/>
										)}
										{!resourceImageUrl && (
											<svg
												className="h-full w-full text-gray-300"
												fill="currentColor"
												viewBox="0 0 24 24">
												<path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
											</svg>
										)}
									</span>
									<button
										onClick={e => setShowResourceImage(true)}
										type="button"
										className="ml-5 rounded-md border border-gray-300 bg-white py-2 px-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
										Change
									</button>
									{resourceImageUrl && (
										<div className="py-2 px-2" onClick={handleResImageTrash}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth={1.5}
												stroke="currentColor"
												className="w-6 h-6">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
												/>
											</svg>
										</div>
									)}
								</div>
							</div>

							{showResourceImage && (
								<>
									<div className="sm:col-span-3">
										<DropUpload
											titleText="Upload an image for the resource display"
											fileText="PNG, JPG, GIF up to 2MB"
											submitFile={submitResourceImageFile}
											maxSize="2000000"
										/>
									</div>
									<div className="sm:col-span-3" />
								</>
							)}
						</div>

						<div className="sm:col-span-6 bg-white p-2 rounded-lg border-2 border-slate-300">
							<div className="sm:col-span-6">
								<div className="relative">
									<div className="absolute inset-0 flex items-center" aria-hidden="true">
										<div className="w-full border-t border-gray-300" />
									</div>
									<div className="relative flex justify-center">
										<span className="bg-white px-3 text-md font-medium text-gray-500">
											Select Resource Type
										</span>
									</div>
								</div>
							</div>
							<div className="sm:col-span-3">
								<div className="pb-4 flex items-center">
									<input
										id="push-everything"
										name="push-notifications"
										type="radio"
										value="LINK"
										checked={type === 'LINK'}
										onChange={handleRadioChange}
										className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<label
										htmlFor="push-everything"
										className="ml-3 block text-sm font-medium text-gray-700">
										Link
									</label>
								</div>
								{type === 'LINK' && (
									<div className="pl-20">
										<div className="mt-1 flex rounded-md shadow-sm">
											<span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
												Display label
											</span>
											<input
												required
												value={documentLabel}
												onChange={handleDocLabelChange}
												type="text"
												name="label"
												id="label"
												className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm invalid:border-red-500"
											/>
										</div>
										<div className="py-4 mt-1 flex rounded-md shadow-sm">
											<span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
												URL
											</span>
											<input
												required
												value={documentLink}
												onChange={handleDocLinkChange}
												type="text"
												name="link"
												id="link"
												className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm invalid:border-red-500"
											/>
										</div>
									</div>
								)}
							</div>
							<div className="sm:col-span-3"></div>

							<div className="sm:col-span-4">
								<div className="pb-4 flex items-center">
									<input
										id="push-everything"
										name="push-notifications"
										type="radio"
										value="INLINE"
										checked={type === 'INLINE'}
										onChange={handleRadioChange}
										className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<label
										htmlFor="push-everything"
										className="ml-3 block text-sm font-medium text-gray-700">
										Inline Document
									</label>
								</div>
								{type === 'INLINE' && (
									<div className="pl-20 mt-1 rich-text">
										<div className="py-4 mt-1 flex rounded-md shadow-sm">
											<span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
												Display label
											</span>
											<input
												required
												value={documentLabel}
												onChange={handleDocLabelChange}
												type="text"
												name="label"
												id="label"
												className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm invalid:border-red-500"
											/>
										</div>

										<div className="border-2">
											<CKEditor
												editor={InlineEditor}
												data={description}
												onReady={editor => {}}
												onChange={(event, editor) => {
													const data = editor.getData();
													setDescription(data);
												}}
											/>
										</div>
									</div>
								)}
							</div>
							<div className="sm:col-span-2"></div>

							<div className="sm:col-span-3">
								<div className="pb-4 flex items-center">
									<input
										id="push-everything"
										name="push-notifications"
										type="radio"
										value="ATTACHMENT"
										checked={type === 'ATTACHMENT'}
										onChange={handleRadioChange}
										className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<label
										htmlFor="push-everything"
										className="ml-3 block text-sm font-medium text-gray-700">
										Attachment
									</label>
								</div>

								{type === 'ATTACHMENT' && (
									<div className="pl-20">
										<div className="mt-1 flex rounded-md shadow-sm">
											<span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
												Display label
											</span>
											<input
												required
												value={documentLabel}
												onChange={handleDocLabelChange}
												type="text"
												name="documentLabel"
												id="documentLabel"
												className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm invalid:border-red-500"
											/>
										</div>
										{!document && !uploading && (
											<DropUpload
												titleText="Upload a file for the resource attachment up to 250MB"
												fileText="PDF, MP4, JPG, PNG, DOCX, XLSX..."
												submitFile={submitFile}
												maxSize="250000000"
											/>
										)}
										<div>
											{uploading && (
												<div className="py-4 grid grid-cols-3">
													<div>
														<Spinner />
													</div>
												</div>
											)}
											{document && !uploading && (
												<div>
													<div className="pt-4 grid grid-cols-3">
														<div className="col-span-2 text-xs font-medium">
															{documentFilename}
														</div>
														<br />
														<div className="text-xs text-gray-700">
															<FileIcon
																type="FILE"
																favicon={document.favicon}
																contentType={document.contentType}
																ext={document.extension}
															/>
															{formatBytes(documentSize)}
															&nbsp;.{document.extension}
														</div>
													</div>
													<div className="py-2" onClick={handleAttachmentTrash}>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															fill="none"
															viewBox="0 0 24 24"
															strokeWidth={1.5}
															stroke="currentColor"
															className="w-6 h-6">
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
															/>
														</svg>
													</div>
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
						<div className="sm:col-span-3">
							<label htmlFor="cover-photo" className="block text-sm font-medium text-gray-700">
								Enabled
							</label>
							<div className="mt-1">
								<Switch
									checked={enabled}
									onChange={() => {
										handleEnable();
									}}
									className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
									<span className="sr-only">Use setting</span>
									<span
										aria-hidden="true"
										className="pointer-events-none absolute h-full w-full rounded-md bg-white"
									/>
									<span
										aria-hidden="true"
										className={classNames(
											enabled ? 'bg-indigo-600' : 'bg-gray-200',
											'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
										)}
									/>
									<span
										aria-hidden="true"
										className={classNames(
											enabled ? 'translate-x-5' : 'translate-x-0',
											'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
										)}
									/>
								</Switch>
							</div>
						</div>
					</div>

					{currentUser.scope && indexOf('admin', currentUser.scope) !== -1 && (
						<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
							<div className="sm:col-span-3">
								<label htmlFor="cover-photo" className="block text-sm font-medium text-gray-700">
									Global
								</label>
								<div className="mt-1">
									<Switch
										checked={global}
										onChange={() => {
											handleGlobal();
										}}
										className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
										<span className="sr-only">Use setting</span>
										<span
											aria-hidden="true"
											className="pointer-events-none absolute h-full w-full rounded-md bg-white"
										/>
										<span
											aria-hidden="true"
											className={classNames(
												global ? 'bg-indigo-600' : 'bg-gray-200',
												'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
											)}
										/>
										<span
											aria-hidden="true"
											className={classNames(
												global ? 'translate-x-5' : 'translate-x-0',
												'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
											)}
										/>
									</Switch>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="pt-5">
				<div className="px-4">
					{resource && (
						<button
							onClick={() => {
								handleDeleteClick();
							}}
							type="button"
							className="m-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
							Delete
						</button>
					)}
					<button
						onClick={() => {
							handleCancelClick();
						}}
						type="button"
						className="m-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
						Cancel
					</button>
					{valid && (
						<button
							onClick={e => {
								handleSubmitClick(e);
							}}
							className="ml-1 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
							Save
						</button>
					)}
				</div>
			</div>
		</form>
	);
};

const LoadingResource = () => {
	return <div>Loading resource</div>;
};

/**
 * Used to load Resource Create/Edit
 * @returns {JSX.Element}
 * @constructor
 */
const ResourceCreatePage = () => {
	const [resource, setResource] = useState(null);
	let { resourceId } = useParams();

	const currentUser = getUser();

	useEffect(() => {
		const fetchResource = async () => {
			const response = await axios({
				method: 'GET',
				url: getResourceURL(resourceId, currentUser.clientIdNum),
			});

			setResource(response.data);
		};

		if (resourceId) {
			fetchResource().catch(console.error);
		}
	}, [resourceId]);

	if (resourceId && !resource) {
		return <LoadingResource />;
	}

	if (!resourceId) {
		return <ResourceCreate />;
	}

	if (resource) {
		return <ResourceCreate resource={resource} />;
	}
};

export default ResourceCreatePage;
